/**
 * Pure helpers for the read-only "Current Price" block on the Instance Details
 * tab. Kept separate from the React component so they can be unit-tested
 * without jsdom, per AGENTS.md.
 *
 * Named `currentPriceUtils` (not `currentPrice`) so it doesn't collide with the
 * `CurrentPrice.jsx` component on case-insensitive filesystems, where webpack
 * would otherwise resolve `./CurrentPrice` to this `.js` file.
 *
 * The current price is the instance's latest sale value (the GraphQL `value`
 * resolver, i.e. the value-table row with `typeId === 2`). It carries the
 * amount (`value`), its `currency` and the `date` it was set.
 */

// How recent a price change must be to earn the "Recently Updated" tag.
export const RECENTLY_UPDATED_DAYS = 10;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Pure: format a sale value as `"USD 250,000"` — currency code + amount with
 * thousands separators. Returns null when there's no usable amount so callers
 * can omit the block entirely.
 */
export const formatCurrentPrice = value => {
  if (value?.value == null) return null;
  const amount = Number(value.value);
  if (Number.isNaN(amount)) return null;
  const code = value?.currency?.code || "";
  return `${code} ${amount.toLocaleString("en-US")}`.trim();
};

/**
 * Pure: was the price set within the last `days` days (and not in the future)?
 * Drives the "Recently Updated" tag. `now` is injectable for testing.
 */
export const isRecentlyUpdated = (
  date,
  { now = Date.now(), days = RECENTLY_UPDATED_DAYS } = {},
) => {
  if (!date) return false;
  const updatedAt = new Date(date).getTime();
  if (Number.isNaN(updatedAt)) return false;
  const ageMs = now - updatedAt;
  return ageMs >= 0 && ageMs <= days * MS_PER_DAY;
};
